import { useState } from 'react';
import { Users, Heart, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const volunteerReasons = [
  { icon: Heart, title: "Make a Difference", description: "Directly impact children's lives" },
  { icon: Users, title: "Join a Community", description: "Connect with like-minded individuals" },
  { icon: CheckCircle, title: "Gain Experience", description: "Build skills while giving back" }
];

const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    availability: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/volunteer`, formData);
      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Volunteer form error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7] py-24" data-testid="volunteer-success">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-20 h-20 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
            Application Received!
          </h2>
          <p className="text-lg text-[#64748B] mb-8">
            Thank you for your interest in volunteering with Protopia. We'll be in touch soon!
          </p>
          <a 
            href="/"
            data-testid="volunteer-home-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="volunteer-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#38BDF8] rounded-full mb-6">
              <Users className="w-8 h-8 text-white" />
            </div>
            <span className="font-accent text-2xl text-[#FF7A59]">Join Our Team</span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
              Become a Volunteer
            </h1>
            <p className="text-xl text-[#64748B] leading-relaxed">
              Join our community of passionate individuals making a difference in the lives of children in Kenya.
            </p>
          </div>
        </div>
      </section>

      {/* Why Volunteer */}
      <section className="py-16 bg-white" data-testid="why-volunteer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] text-center mb-12">
            Why Volunteer With Us?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {volunteerReasons.map((reason, index) => (
              <div 
                key={index}
                data-testid={`volunteer-reason-${index}`}
                className="rounded-3xl bg-[#FDFBF7] p-8 text-center"
              >
                <div className="w-16 h-16 bg-[#FACC15] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <reason.icon className="w-8 h-8 text-[#422006]" />
                </div>
                <h3 className="text-xl font-bold text-[#1E293B] font-['Fredoka'] mb-2">
                  {reason.title}
                </h3>
                <p className="text-[#64748B]">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Form */}
      <section className="py-16 bg-[#FDFBF7]" data-testid="volunteer-form-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <h2 className="text-2xl font-bold text-[#1E293B] font-['Fredoka'] mb-6 text-center">
              Volunteer Application
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-[#1E293B] font-semibold">
                    Full Name <span className="text-[#FF7A59]">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="volunteer-name-input"
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1E293B] font-semibold">
                    Email <span className="text-[#FF7A59]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="volunteer-email-input"
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#1E293B] font-semibold">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="volunteer-phone-input"
                  className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <Label htmlFor="skills" className="text-[#1E293B] font-semibold">
                  Skills & Expertise
                </Label>
                <Input
                  id="skills"
                  type="text"
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                  data-testid="volunteer-skills-input"
                  className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                  placeholder="e.g., Event planning, Teaching, Photography"
                />
              </div>

              <div>
                <Label htmlFor="availability" className="text-[#1E293B] font-semibold">
                  Availability
                </Label>
                <Select
                  value={formData.availability}
                  onValueChange={(value) => setFormData({ ...formData, availability: value })}
                >
                  <SelectTrigger 
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    data-testid="volunteer-availability-select"
                  >
                    <SelectValue placeholder="Select your availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekends">Weekends Only</SelectItem>
                    <SelectItem value="weekdays">Weekdays Only</SelectItem>
                    <SelectItem value="flexible">Flexible Schedule</SelectItem>
                    <SelectItem value="events">Event Days Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#1E293B] font-semibold">
                  Tell Us About Yourself
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="volunteer-message-input"
                  className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15] min-h-[120px]"
                  placeholder="Share why you want to volunteer with Protopia..."
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                data-testid="volunteer-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#38BDF8] text-white font-bold px-8 py-4 shadow-sm hover:bg-[#38BDF8]/90 hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
